import { useState } from 'react';
import { D, formatMoney } from '@makila/fiscal';
import { WORK_LABEL, type Order, type WorkStatus } from '@makila/pos-core';
import { Barcode, ClipboardList, Search } from 'lucide-react';
import clsx from 'clsx';
import { Alert, Badge, Card, Dialog, EmptyState, Input, Skeleton } from './ui';
import { useLoad } from './useLoad';

const dmy = (d: string) => d.slice(0, 10).split('-').reverse().join('/');
const when = (iso: string) => new Date(iso).toLocaleString('pt-PT', { timeZone: 'Africa/Maputo', dateStyle: 'short', timeStyle: 'short' });

interface WorkRow {
  id: string; store: string; number: string; customer: { name: string; phone: string | null }; object: string; identifier: string | null; problem: string;
  status: WorkStatus; promisedOn: string | null; pieces: number | null; late: boolean; total: string; receivedAt: string; updatedAt: string; documents: string[];
  quote: { number: string; total: string; approvedAt?: string | null; refusedAt?: string | null } | null;
}
const TONE: Record<WorkStatus, 'neutral' | 'info' | 'ok' | 'warn' | 'brand'> = { received: 'neutral', in_progress: 'info', ready: 'ok', delivered: 'neutral', cancelled: 'warn' };

/**
 * Ordens de serviço (docs/14 Fase 5): o que está na oficina ou na loja, por
 * estado, as atrasadas e as prontas por levantar. Recebe-se, trabalha-se e
 * entrega-se na caixa (Ordens); aqui consulta-se.
 */
export function WorkOrdersPage() {
  const [tab, setTab] = useState<'open' | 'ready' | 'late' | 'closed'>('open');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, error } = useLoad<{ items: WorkRow[]; counts: { received: number; in_progress: number; ready: number; late: number } }>(`/workorders?status=${tab}${q.trim().length >= 2 ? `&q=${encodeURIComponent(q.trim())}` : ''}`);
  const c = data?.counts;
  const tabs = [['open', `Abertas${c ? ` (${c.received + c.in_progress + c.ready})` : ''}`], ['ready', `Prontas${c ? ` (${c.ready})` : ''}`], ['late', `Atrasadas${c ? ` (${c.late})` : ''}`], ['closed', 'Entregues e anuladas']] as const;
  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" className="flex flex-wrap gap-1">
          {tabs.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={clsx('h-11 rounded-lg px-4 font-medium', tab === k ? 'bg-brand-700 text-white' : 'border border-base')}>{l}</button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input aria-label="Procurar ordem" placeholder="Número, cliente, matrícula, IMEI…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-9" />
        </div>
      </div>
      {c && tab === 'open' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {([['Recebidas', c.received], ['Em curso', c.in_progress], ['Prontas', c.ready], ['Atrasadas', c.late]] as const).map(([l, n]) => (
            <div key={l} className="surface rounded-xl p-4"><div className="text-sm text-muted">{l}</div><div className={clsx('num text-2xl font-bold', l === 'Atrasadas' && n ? 'text-brand-700' : '')}>{n}</div></div>
          ))}
        </div>
      )}
      {error && <Alert>{error}</Alert>}
      {!data ? <Skeleton rows={4} /> : !data.items.length ? (
        <EmptyState icon={ClipboardList} title={tab === 'closed' ? 'Nada entregue ainda' : 'Sem ordens'} text="As ordens de serviço registam-se na caixa, em Ordens: quem deixou, o quê, e quando fica pronto." />
      ) : (
        <Card padded={false} title={`${data.items.length} ordem(ns)`} description="Clique numa ordem para ver o que se fez, o orçamento e as fotografias.">
          <ul className="divide-y divide-[var(--border)]">
            {data.items.map((o) => (
              <li key={o.id}>
                <button onClick={() => setOpenId(o.id)} className="flex w-full flex-wrap items-start gap-3 px-4 py-3 text-left hover:bg-surface-2 xl:px-6">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-mono font-bold">{o.number}</span>
                      <span className="font-medium">{o.customer.name}</span>
                      {o.customer.phone && <span className="text-xs text-muted">{o.customer.phone}</span>}
                    </div>
                    <div className="text-sm">{o.object}{o.pieces ? ` · ${o.pieces} peça(s)` : ''}{o.identifier ? <span className="ml-1 font-mono text-xs text-muted">{o.identifier}</span> : null}</div>
                    <div className="text-xs text-muted">{o.store} · recebida {when(o.receivedAt)}{o.documents.length ? ` · ${o.documents.join(', ')}` : ''}</div>
                  </div>
                  <div className="num space-y-1 text-right text-sm">
                    <Badge tone={o.late ? 'brand' : TONE[o.status]}>{o.late ? 'atrasada' : WORK_LABEL[o.status].toLowerCase()}</Badge>
                    {o.promisedOn && <div className={o.late ? 'font-semibold text-brand-700' : 'text-muted'}>pronta a {dmy(o.promisedOn)}</div>}
                    {!new D(o.total).isZero() && <div><b>{formatMoney(o.total)}</b> MT</div>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {openId && <WorkOrderDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function WorkOrderDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error } = useLoad<WorkRow & { order: Order; photos: { id: string; data: string }[]; operators: Record<string, string> }>(`/workorders/${id}`);
  const w = data?.order.work;
  return (
    <Dialog title={data ? `Ordem ${data.number}` : 'Ordem de serviço'} size="lg" onClose={onClose}>
      {error && <Alert>{error}</Alert>}
      {!data || !w ? <Skeleton rows={5} /> : (
        <div className="space-y-4 text-sm">
          <div className="grid gap-1 sm:grid-cols-2">
            <div><span className="text-muted">Cliente:</span> <b>{w.customer.name}</b>{w.customer.phone ? ` · ${w.customer.phone}` : ''}</div>
            <div><span className="text-muted">Estado:</span> {WORK_LABEL[w.status]}{data.late ? ' (atrasada)' : ''}</div>
            <div><span className="text-muted">Objecto:</span> {w.object}{w.pieces ? ` · ${w.pieces} peça(s)` : ''}</div>
            {w.identifier && <div><span className="text-muted">Identificação:</span> <span className="font-mono">{w.identifier}</span></div>}
            {w.technicianId && <div><span className="text-muted">Técnico:</span> {data.operators[w.technicianId] ?? '—'}</div>}
            {w.promisedOn && <div><span className="text-muted">Pronta a:</span> {dmy(w.promisedOn)}</div>}
            {w.warranty && <div className="sm:col-span-2"><span className="text-muted">Vendido por nós:</span> {w.warranty.document} de {dmy(w.warranty.soldOn)}{w.warranty.until ? ` · garantia até ${dmy(w.warranty.until)}` : ''}</div>}
            {w.problem && <div className="sm:col-span-2"><span className="text-muted">Pedido:</span> {w.problem}</div>}
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Serviços e peças</h3>
            <ul className="divide-y divide-[var(--border)] rounded-lg border border-base">
              {data.order.items.filter((i) => i.status !== 'cancelled').map((i) => (
                <li key={i.id} className="flex justify-between gap-3 px-3 py-2">
                  <span>{i.description}{i.serial ? <span className="ml-1 font-mono text-xs text-muted">n.º série {i.serial}</span> : null}{i.paidIn ? <span className="ml-1 text-xs text-muted">· {i.paidIn}</span> : null}</span>
                  <span className="num">{i.quantity.replace('.', ',')} × {formatMoney(i.unitPrice)}</span>
                </li>
              ))}
              {!data.order.items.some((i) => i.status !== 'cancelled') && <li className="px-3 py-2 text-muted">Nada.</li>}
            </ul>
          </div>
          {w.quote && <p><span className="text-muted">Orçamento:</span> {w.quote.number} · {formatMoney(w.quote.total)} MT · {w.quote.approvedAt ? 'aprovado' : w.quote.refusedAt ? 'recusado' : 'por responder'}</p>}
          {w.notes && <p className="whitespace-pre-line"><span className="text-muted">Notas:</span> {w.notes}</p>}
          {data.photos.length > 0 && <div className="grid grid-cols-3 gap-2">{data.photos.map((f) => <img key={f.id} src={f.data} alt="Fotografia da recepção" className="aspect-square w-full rounded-lg object-cover" />)}</div>}
          <div>
            <h3 className="mb-1 font-semibold">Histórico</h3>
            <ol className="space-y-0.5">{w.history.map((h) => <li key={`${h.status}${h.at}`}><span className="num text-muted">{when(h.at)}</span> · {WORK_LABEL[h.status]}{data.operators[h.by] ? ` · ${data.operators[h.by]}` : ''}</li>)}</ol>
          </div>
        </div>
      )}
    </Dialog>
  );
}

interface SerialRow { id: string; serial: string; description: string; full_number: string; sold_on: string; customer_name: string | null; warranty_until: string | null; returned_on: string | null; store_name: string | null; in_warranty: boolean }

/** "Este IMEI foi vendido quando e a quem?": procura pelo número de série ou pelo cliente. */
export function SerialsPage() {
  const [q, setQ] = useState('');
  const term = q.trim();
  const { data, error } = useLoad<{ items: SerialRow[] }>(term.length >= 2 ? `/serials?q=${encodeURIComponent(term)}` : '');
  return (
    <div className="max-w-5xl space-y-4">
      <Card title="Números de série" description="Cada unidade vendida com número de série ou IMEI: o documento, o cliente e a garantia. A devolução (nota de crédito) marca-a devolvida.">
        <div className="relative">
          <Barcode size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input autoFocus aria-label="Número de série ou cliente" placeholder="Leia ou escreva o IMEI, o n.º de série ou o nome do cliente" value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-10" />
        </div>
      </Card>
      {error && <Alert>{error}</Alert>}
      {term.length < 2 ? null : !data ? <Skeleton rows={3} /> : !data.items.length ? <p className="text-center text-muted">Nenhuma venda com esse número ou cliente.</p> : (
        <Card padded={false}>
          <ul className="divide-y divide-[var(--border)]">
            {data.items.map((s) => (
              <li key={s.id} className="flex flex-wrap items-start gap-3 px-4 py-3 xl:px-6">
                <div className="min-w-0 flex-1">
                  <div className="font-mono font-bold">{s.serial}</div>
                  <div className="text-sm">{s.description}</div>
                  <div className="text-xs text-muted">{s.full_number} · {dmy(s.sold_on)} · {s.customer_name ?? 'Consumidor final'}{s.store_name ? ` · ${s.store_name}` : ''}</div>
                </div>
                <div className="text-right text-sm">
                  {s.returned_on ? <Badge tone="warn">devolvido a {dmy(s.returned_on)}</Badge>
                    : s.in_warranty ? <Badge tone="ok">na garantia até {dmy(s.warranty_until!)}</Badge>
                      : <Badge>{s.warranty_until ? `garantia acabou a ${dmy(s.warranty_until)}` : 'sem garantia'}</Badge>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
